import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { logEvent } from '../utils/analytics';

import {
    Utensils,
    Droplets,
    Dumbbell,
    Scale,
    MessageSquare,
    X,
    ChevronLeft,
    ChevronRight,
    Target,
    Activity,
    ShieldCheck,
    LayoutDashboard,
    Sparkles,
    ArrowRight,
    CheckCircle2,
    Play,
} from 'lucide-react';

const container: Variants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.08,
        },
    },
};

const item: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const APP_FEATURES = [
    {
        id: 'dashboard',
        title: 'Daily Dashboard',
        icon: LayoutDashboard,
        brief: 'See calories, steps, hydration, weight, and key insights in one motivating daily snapshot.',
        mission:
            'To give users a clear command center for the day, so they always know where they stand and what to do next.',
        workflow: [
            'Open the dashboard to instantly see calories left, burn, hydration, and latest weight.',
            'Review smart progress cards that surface the signals that matter most.',
            'Use quick actions to log faster and stay consistent throughout the day.',
        ],
        value:
            'Brings your most important metrics, actions, and AI insights into one screen so decision-making feels easy.',
        highlight: 'Your whole day in one glance',
        images: ['/dashboard1.jpeg', '/dashboard2.jpeg'],
    },
    {
        id: 'meals',
        title: 'AI Meal Insights',
        icon: Utensils,
        brief: 'Log meals fast and get instant calorie, macro, fibre, and satiety-focused feedback.',
        mission:
            'To remove the guesswork from eating by helping users understand how every meal supports their goal.',
        workflow: [
            'Log food with smart text, barcode scanning, or quick entry.',
            'LesslyKilo estimates calories, macros, and fibre automatically.',
            'AI deep-dives highlight imbalances and suggest one practical next step.',
        ],
        value:
            'Turns food logging into actionable guidance, helping users improve meals instead of just recording them.',
        highlight: 'Know what each meal is doing',
        images: ['/logFood.jpeg', '/mealInsights2.jpeg', '/foodLog2.jpeg'],
    },
    {
        id: 'water',
        title: 'Smart Hydration',
        icon: Droplets,
        brief: 'Simple water logging with intelligent hydration targets and consistency feedback.',
        mission:
            'To help users stay hydrated in a way that supports energy, appetite control, and sustainable fat loss.',
        workflow: [
            'Log water intake in seconds.',
            'Track progress against your daily hydration goal.',
            'Use AI insights to spot patterns and improve consistency over time.',
        ],
        value:
            'Helps users stay on top of hydration without effort, reducing missed goals and supporting better daily habits.',
        highlight: 'Hydration that supports results',
        images: ['/logWater.jpeg', '/hydrationInsight1.jpeg', '/hydrationInsight2.jpeg'],
    },
    {
        id: 'workout',
        title: 'Workout Tracking & Insights',
        icon: Dumbbell,
        brief: 'Log workouts quickly and understand how activity affects your progress.',
        mission:
            'To make workout tracking useful and motivating by connecting movement to burn, recovery, and daily progress.',
        workflow: [
            'Quick-log workouts or sync supported activity data.',
            'See how training contributes to active burn and energy balance.',
            'Get AI insights on how workouts fit into your overall strategy.',
        ],
        value:
            'Makes workouts feel connected to the bigger picture, helping users see how movement supports progress.',
        highlight: 'Movement with context',
        images: ['/logWorkout.jpeg', '/logWorkout2.jpeg', '/workoutInsights.jpeg', '/workoutInsights2.jpeg'],
    },
    {
        id: 'weight',
        title: 'Progress & Trend Analysis',
        icon: Scale,
        brief: 'See your weight trend clearly and understand what is actually driving change over time.',
        mission:
            'To turn isolated weigh-ins into a progress story that keeps users patient, informed, and motivated.',
        workflow: [
            'Log weight daily or weekly.',
            'Compare weight trends against meals, hydration, and activity.',
            'Let AI explain likely drivers behind change and what to focus on next.',
        ],
        value:
            'Helps users look beyond day-to-day noise and stay anchored to meaningful long-term progress.',
        highlight: 'Look beyond the scale',
        images: ['/logWeight.jpeg', '/weightInsight1.jpeg', '/weightInsight2.jpeg'],
    },
    {
        id: 'coach',
        title: 'Interactive AI Coach',
        icon: MessageSquare,
        brief: 'Get daily guidance, weekly reviews, and on-demand help when motivation dips.',
        mission:
            'To give users practical behavioral support so they always know their next best step.',
        workflow: [
            'Receive daily coaching prompts and weekly progress reflections.',
            'Chat with the coach for help with cravings, setbacks, or tough choices.',
            'Use AI guidance to recover quickly and build stronger habits over time.',
        ],
        value:
            'Adds accountability, clarity, and support that keep users engaged and moving forward.',
        highlight: 'Guidance when you need it most',
        images: ['/coachHome.jpeg', '/askCoach.jpeg', '/dailyCoach.jpeg'],
    },
] as const;

type Feature = (typeof APP_FEATURES)[number];

export default function Features() {
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);


    useEffect(() => {
        if (selectedFeature) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            setCurrentImageIndex(0);
        } else {
            document.body.style.overflow = 'unset';
            document.documentElement.style.overflow = 'unset';
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (!selectedFeature) return;
            if (e.key === 'Escape') setSelectedFeature(null);
            if (e.key === 'ArrowRight') nextImage();
            if (e.key === 'ArrowLeft') prevImage();
        };

        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = 'unset';
            document.documentElement.style.overflow = 'unset';
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedFeature]);

    const nextImage = () => {
        if (!selectedFeature) return;
        setCurrentImageIndex((prev) => (prev + 1) % selectedFeature.images.length);
    };

    const prevImage = () => {
        if (!selectedFeature) return;
        setCurrentImageIndex((prev) => (prev - 1 + selectedFeature.images.length) % selectedFeature.images.length);
    };

    return (
        <section
            id="features"
            className="relative overflow-hidden py-24 md:py-32"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-[-10%] top-10 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute right-[-10%] top-1/3 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.04),transparent_35%)]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5 }}
                    className="mx-auto mb-16 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)]/70 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)]">
                        <Sparkles size={14} />
                        Everything works together
                    </div>

                    <h2 className="text-balance text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        LesslyKilo turns daily tracking into
                        <span className="block bg-gradient-to-r from-[var(--color-brand-primary)] to-emerald-400 bg-clip-text text-transparent">
              smarter weight-loss decisions
            </span>
                    </h2>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        Track your meals, hydration, workouts, weight, and daily progress in one connected system,
                        then use AI-powered insights to understand what is working and what to do next.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        {[
                            'Fast daily logging',
                            'Personalized AI insights',
                            'Built for sustainable results',
                        ].map((label) => (
                            <div
                                key={label}
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]"
                            >
                                <CheckCircle2 size={16} className="text-[var(--color-brand-success)]" />
                                {label}
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Feature cards */}
                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
                >
                    {APP_FEATURES.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <motion.button
                                key={feature.id}
                                variants={item}
                                whileHover={{ y: -6, scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                                onClick={() => {
                                    setSelectedFeature(feature);
                                    // Track exactly which clinical feature they are interested in
                                    logEvent('Feature_Engagement', 'Open_Feature_Modal', feature.title);
                                }}
                                className="group relative overflow-hidden rounded-[2rem] border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] p-0 text-left shadow-[0_8px_30px_rgba(0,0,0,0.18)] transition-all hover:border-[var(--color-brand-primary)]/50 hover:shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
                            >
                                {/* visual preview */}
                                <div className="relative h-56 overflow-hidden border-b border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)]">
                                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-brand-primary)]/10 via-transparent to-emerald-400/10" />
                                    <img
                                        src={feature.images[0]}
                                        alt={feature.title}
                                        className="h-full w-full object-cover object-top opacity-90 transition-transform duration-500 group-hover:scale-[1.03]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-brand-card)] via-[var(--color-brand-card)]/25 to-transparent" />

                                    <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/35 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/90 backdrop-blur-md">
                                        <span className="text-[var(--color-brand-primary)]">0{index + 1}</span>
                                        Feature
                                    </div>

                                    <div className="absolute bottom-5 left-5 right-5">
                                        <div className="mb-3 inline-flex items-center gap-2 rounded-2xl bg-[var(--color-brand-primary)]/12 px-3 py-2 text-sm font-semibold text-[var(--color-brand-primary)] backdrop-blur-md">
                                            <Icon size={16} />
                                            {feature.highlight}
                                        </div>
                                    </div>
                                </div>

                                {/* content */}
                                <div className="p-7">
                                    <div className="mb-4 flex items-start justify-between gap-4">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)] transition-all duration-300 group-hover:bg-[var(--color-brand-primary)] group-hover:text-white">
                                            <Icon size={28} />
                                        </div>

                                        <div className="rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[var(--color-brand-text-secondary)]">
                                            Tap to explore
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black tracking-tight text-[var(--color-brand-text)]">
                                        {feature.title}
                                    </h3>

                                    <p className="mt-3 text-[15px] leading-7 text-[var(--color-brand-text-secondary)]">
                                        {feature.brief}
                                    </p>

                                    <div className="mt-6 flex items-center justify-between">
                                        <div className="text-sm font-semibold text-[var(--color-brand-primary)]">
                                            See feature details
                                        </div>
                                        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] text-[var(--color-brand-text)] transition-colors group-hover:border-[var(--color-brand-primary)]/40 group-hover:text-[var(--color-brand-primary)]">
                                            <ArrowRight size={18} />
                                        </div>
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </motion.div>

                {/* Bottom CTA strip */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.08 }}
                    className="mt-14 rounded-[2rem] border border-[var(--color-brand-border)] bg-gradient-to-br from-[var(--color-brand-card)] to-[var(--color-brand-ai-bg)] p-8 md:p-10"
                >
                    <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                        <div className="max-w-2xl">
                            <h3 className="text-2xl font-black text-[var(--color-brand-text)] md:text-3xl">
                                Built to help users stay consistent, not overwhelmed
                            </h3>
                            <p className="mt-3 text-[15px] leading-7 text-[var(--color-brand-text-secondary)] md:text-base">
                                LesslyKilo combines tracking, behavior support, and AI guidance into one experience,
                                so users can log less, understand more, and make better decisions every day.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            {/*<a
                                href="#download"
                                className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand-primary)] px-6 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
                            >
                                Get the app
                                <ArrowRight size={16} />
                            </a>*/}
                            <a
                                href="#pricing"
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-6 py-3 text-sm font-bold text-[var(--color-brand-text)] transition-colors hover:border-[var(--color-brand-primary)]/40 hover:text-[var(--color-brand-primary)]"
                            >
                                View plans
                            </a>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {selectedFeature && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedFeature(null)}
                        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 30, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 24, scale: 0.98 }}
                            transition={{ duration: 0.28, ease: 'easeOut' }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative mx-auto flex h-[100dvh] w-full flex-col overflow-hidden bg-[var(--color-brand-card)] sm:mt-4 sm:h-auto sm:max-h-[92vh] sm:max-w-6xl sm:rounded-[2rem] sm:border sm:border-[var(--color-brand-border)] sm:shadow-2xl"
                        >
                            <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
                                {/* Sticky mobile-first top bar */}
                                <div className="sticky top-0 z-30 border-b border-[var(--color-brand-border)] bg-[color:var(--color-brand-card)]/92 backdrop-blur-xl">
                                    <div className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6">
                                        <div className="min-w-0">
                                            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand-primary)]/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                                {React.createElement(selectedFeature.icon, { size: 14 })}
                                                {selectedFeature.id}
                                            </div>
                                            <h3 className="mt-2 truncate text-lg font-black text-[var(--color-brand-text)] sm:text-xl">
                                                {selectedFeature.title}
                                            </h3>
                                        </div>

                                        <button
                                            onClick={() => setSelectedFeature(null)}
                                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] text-[var(--color-brand-text)] transition-colors hover:border-[var(--color-brand-primary)]/40 hover:text-[var(--color-brand-primary)]"
                                            aria-label="Close feature details"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile-first stacked layout, desktop split later */}
                                <div className="lg:grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                                    {/* Preview section */}
                                    <div className="border-b border-[var(--color-brand-border)] bg-[linear-gradient(180deg,var(--color-brand-ai-bg),var(--color-brand-card))] p-4 sm:p-6 lg:min-h-full lg:border-b-0 lg:border-r lg:p-8">
                                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                            <Play size={13} />
                                            Feature preview
                                        </div>

                                        {/* Phone mockup */}
                                        <div className="relative mx-auto w-full max-w-[290px] sm:max-w-[320px]">
                                            <div className="absolute inset-0 rounded-full bg-[var(--color-brand-primary)]/10 blur-3xl" />

                                            <div className="relative rounded-[2.5rem] border-[10px] border-[#111827] bg-black shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
                                                <div className="absolute left-1/2 top-0 z-10 h-7 w-28 -translate-x-1/2 rounded-b-2xl bg-[#111827]" />
                                                <div className="aspect-[9/19.5] overflow-hidden rounded-[2rem]">
                                                    <AnimatePresence mode="wait">
                                                        <motion.img
                                                            key={currentImageIndex}
                                                            src={selectedFeature.images[currentImageIndex]}
                                                            alt={`${selectedFeature.title} screenshot ${currentImageIndex + 1}`}
                                                            initial={{ opacity: 0, x: 16 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: -16 }}
                                                            transition={{ duration: 0.26 }}
                                                            className="h-full w-full object-cover object-top"
                                                        />
                                                    </AnimatePresence>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Screenshot controls */}
                                        {selectedFeature.images.length > 1 && (
                                            <div className="mt-5">
                                                <div className="flex items-center justify-center gap-3">
                                                    <button
                                                        onClick={prevImage}
                                                        className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] text-[var(--color-brand-text)] transition-colors hover:border-[var(--color-brand-primary)]/40 hover:text-[var(--color-brand-primary)]"
                                                        aria-label="Previous screenshot"
                                                    >
                                                        <ChevronLeft size={18} />
                                                    </button>

                                                    <div className="flex items-center gap-2">
                                                        {selectedFeature.images.map((_, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => setCurrentImageIndex(idx)}
                                                                className={`h-2.5 rounded-full transition-all ${
                                                                    idx === currentImageIndex
                                                                        ? 'w-8 bg-[var(--color-brand-primary)]'
                                                                        : 'w-2.5 bg-[var(--color-brand-border)]'
                                                                }`}
                                                                aria-label={`Go to screenshot ${idx + 1}`}
                                                            />
                                                        ))}
                                                    </div>

                                                    <button
                                                        onClick={nextImage}
                                                        className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] text-[var(--color-brand-text)] transition-colors hover:border-[var(--color-brand-primary)]/40 hover:text-[var(--color-brand-primary)]"
                                                        aria-label="Next screenshot"
                                                    >
                                                        <ChevronRight size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Quick summary cards */}
                                        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                                            <div className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] p-4">
                                                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                                    Outcome
                                                </div>
                                                <div className="mt-2 text-sm font-semibold text-[var(--color-brand-text)]">
                                                    Faster daily decisions
                                                </div>
                                            </div>

                                            <div className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] p-4">
                                                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                                    Experience
                                                </div>
                                                <div className="mt-2 text-sm font-semibold text-[var(--color-brand-text)]">
                                                    Clear, guided, actionable
                                                </div>
                                            </div>

                                            <div className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] p-4">
                                                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                                    Built for
                                                </div>
                                                <div className="mt-2 text-sm font-semibold text-[var(--color-brand-text)]">
                                                    Sustainable consistency
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content section */}
                                    <div className="p-4 sm:p-6 lg:p-8">
                                        <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand-primary)]/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                            {React.createElement(selectedFeature.icon, { size: 14 })}
                                            {selectedFeature.highlight}
                                        </div>

                                        <h3 className="mt-4 text-2xl font-black tracking-tight text-[var(--color-brand-text)] sm:text-3xl lg:text-4xl">
                                            {selectedFeature.title}
                                        </h3>

                                        <p className="mt-4 text-base leading-8 text-[var(--color-brand-text-secondary)] sm:text-lg">
                                            {selectedFeature.brief}
                                        </p>

                                        <div className="mt-6 space-y-4 sm:space-y-5">
                                            <div className="rounded-[1.5rem] border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] p-5 sm:p-6">
                                                <h4 className="mb-3 flex items-center gap-2 text-base font-bold text-[var(--color-brand-text)] sm:text-lg">
                                                    <Target size={18} className="text-[var(--color-brand-primary)]" />
                                                    Why it matters
                                                </h4>
                                                <p className="leading-7 text-[var(--color-brand-text-secondary)]">
                                                    {selectedFeature.mission}
                                                </p>
                                            </div>

                                            <div className="rounded-[1.5rem] border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] p-5 sm:p-6">
                                                <h4 className="mb-4 flex items-center gap-2 text-base font-bold text-[var(--color-brand-text)] sm:text-lg">
                                                    <Activity size={18} className="text-[var(--color-brand-primary)]" />
                                                    How it works
                                                </h4>

                                                <ul className="space-y-4">
                                                    {selectedFeature.workflow.map((step, idx) => (
                                                        <li key={idx} className="flex items-start gap-4">
                                                            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-brand-primary)]/12 text-sm font-black text-[var(--color-brand-primary)]">
                                                                {idx + 1}
                                                            </div>
                                                            <span className="leading-7 text-[var(--color-brand-text-secondary)]">
                                                    {step}
                                                </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="rounded-[1.5rem] border border-emerald-500/20 bg-emerald-500/5 p-5 sm:p-6">
                                                <h4 className="mb-3 flex items-center gap-2 text-base font-bold text-[var(--color-brand-text)] sm:text-lg">
                                                    <ShieldCheck size={18} className="text-[var(--color-brand-success)]" />
                                                    User value
                                                </h4>
                                                <p className="leading-7 text-[var(--color-brand-text-secondary)]">
                                                    {selectedFeature.value}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-8 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap">
                                            <a
                                                href="#download"
                                                onClick={() => setSelectedFeature(null)}
                                                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--color-brand-primary)] px-6 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
                                            >
                                                Get LesslyKilo
                                                <ArrowRight size={16} />
                                            </a>

                                            <a
                                                href="#pricing"
                                                onClick={() => setSelectedFeature(null)}
                                                className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-6 py-3 text-sm font-bold text-[var(--color-brand-text)] transition-colors hover:border-[var(--color-brand-primary)]/40 hover:text-[var(--color-brand-primary)]"
                                            >
                                                See pricing
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}